import http from "@/helper/http";
import { RESOURCE_CACHE } from "@/config/queryConfig";

// Request deduplication cache
const pendingRequests = new Map();

// Data cache with timestamps
const dataCache = new Map();

// Expose cache for clearing (used by useCacheClear composable)
if (typeof window !== "undefined") {
  window.__VUEX_DATA_CACHE__ = dataCache;
}

const state = {
  // Transfers (completed)
  transfers: [],
  transferDetails: null,
  loadingTransfers: false,
  loadingTransferDetails: false,

  // Transfer Requests (pending/in-progress)
  transferRequests: [],
  transferRequestDetails: null,
  loadingTransferRequests: false,
  loadingTransferRequestDetails: false,

  // Inventory Removals
  removals: [],
  loadingRemovals: false,
  removalsSummary: {
    total_amount: 0,
    total_quantity_removed: 0,
    currency_code: "RWF",
  },

  // Pagination
  transfersPagination: {
    page: 1,
    per_page: 20,
    total: 0,
    pages: 0,
  },
  requestsPagination: {
    page: 1,
    per_page: 20,
    total: 0,
    pages: 0,
  },
  removalsPagination: {
    page: 1,
    per_page: 20,
    total: 0,
    pages: 0,
  },

  // Filters
  transferFilters: {
    from_store_id: null,
    to_store_id: null,
    status: null,
  },
  requestFilters: {
    store_id: null,
    other_store_id: null,
    status: null,
  },

  // Error handling
  error: null,
};

const getters = {
  // Transfers
  transfers: (state) => state.transfers,
  transferDetails: (state) => state.transferDetails,
  loadingTransfers: (state) => state.loadingTransfers,
  loadingTransferDetails: (state) => state.loadingTransferDetails,
  transfersPagination: (state) => state.transfersPagination,

  // Transfer Requests
  transferRequests: (state) => state.transferRequests,
  transferRequestDetails: (state) => state.transferRequestDetails,
  loadingTransferRequests: (state) => state.loadingTransferRequests,
  loadingTransferRequestDetails: (state) => state.loadingTransferRequestDetails,
  requestsPagination: (state) => state.requestsPagination,

  // Removals
  removals: (state) => state.removals,
  loadingRemovals: (state) => state.loadingRemovals,
  removalsPagination: (state) => state.removalsPagination,
  removalsSummary: (state) => state.removalsSummary,

  // Errors
  error: (state) => state.error,

  // Computed helpers
  pendingRequests: (state) =>
    state.transferRequests.filter((r) => r.status === "PENDING"),
  inFulfillmentRequests: (state) =>
    state.transferRequests.filter((r) => r.status === "IN_FULFILLMENT"),
};

const mutations = {
  // Transfers
  SET_TRANSFERS(state, transfers) {
    state.transfers = transfers;
  },
  SET_TRANSFER_DETAILS(state, details) {
    state.transferDetails = details;
  },
  SET_LOADING_TRANSFERS(state, loading) {
    state.loadingTransfers = loading;
  },
  SET_LOADING_TRANSFER_DETAILS(state, loading) {
    state.loadingTransferDetails = loading;
  },
  SET_TRANSFERS_PAGINATION(state, pagination) {
    state.transfersPagination = { ...state.transfersPagination, ...pagination };
  },
  SET_TRANSFER_FILTERS(state, filters) {
    state.transferFilters = { ...state.transferFilters, ...filters };
  },

  // Transfer Requests
  SET_TRANSFER_REQUESTS(state, requests) {
    state.transferRequests = requests;
  },
  SET_TRANSFER_REQUEST_DETAILS(state, details) {
    state.transferRequestDetails = details;
  },
  SET_LOADING_TRANSFER_REQUESTS(state, loading) {
    state.loadingTransferRequests = loading;
  },
  SET_LOADING_TRANSFER_REQUEST_DETAILS(state, loading) {
    state.loadingTransferRequestDetails = loading;
  },
  SET_REQUESTS_PAGINATION(state, pagination) {
    state.requestsPagination = { ...state.requestsPagination, ...pagination };
  },
  SET_REQUEST_FILTERS(state, filters) {
    state.requestFilters = { ...state.requestFilters, ...filters };
  },
  UPDATE_REQUEST_STATUS(state, { requestId, status }) {
    const request = state.transferRequests.find((r) => r.id === requestId);
    if (request) {
      request.status = status;
    }
    if (state.transferRequestDetails?.id === requestId) {
      state.transferRequestDetails.status = status;
    }
  },

  // Removals
  SET_REMOVALS(state, removals) {
    state.removals = removals;
  },
  SET_LOADING_REMOVALS(state, loading) {
    state.loadingRemovals = loading;
  },
  SET_REMOVALS_PAGINATION(state, pagination) {
    state.removalsPagination = { ...state.removalsPagination, ...pagination };
  },
  SET_REMOVALS_SUMMARY(state, summary) {
    state.removalsSummary = {
      total_amount: 0,
      total_quantity_removed: 0,
      currency_code: "RWF",
      ...(summary || {}),
    };
  },

  // Error
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
};

const actions = {
  // ==================== TRANSFERS ====================

  async fetchTransfers(
    { commit, state },
    { groupId, filters = {}, page, perPage, forceRefresh = false } = {},
  ) {
    // Create cache key with stable filter serialization
    const stableFilters = Object.keys(filters)
      .sort()
      .map((key) => `${key}:${filters[key]}`)
      .join("|");
    const cacheKey = `transfers-${groupId}-${stableFilters}-${
      page || state.transfersPagination.page
    }-${perPage || state.transfersPagination.per_page}`;

    // Check cache first (unless forceRefresh)
    if (!forceRefresh && dataCache.has(cacheKey)) {
      const cached = dataCache.get(cacheKey);
      const age = Date.now() - cached.timestamp;
      const maxAge = RESOURCE_CACHE.transfers.staleTime;

      if (age < maxAge) {
        console.log(
          `✅ Using cached transfers (${Math.round(
            age / 1000,
          )}s old) - No loading state`,
        );
        // Don't set loading state for cached data
        commit("SET_TRANSFERS", cached.data.items || []);
        commit("SET_TRANSFERS_PAGINATION", {
          page: cached.data.page,
          per_page: cached.data.per_page,
          total: cached.data.total,
          pages: cached.data.pages,
        });
        return cached.data;
      } else {
        console.log(
          `♻️ Cache expired (${Math.round(
            age / 1000,
          )}s old), fetching fresh data`,
        );
        dataCache.delete(cacheKey);
      }
    }

    // Check if already fetching (deduplication)
    if (pendingRequests.has(cacheKey)) {
      console.log("⏭️ Deduplicating transfers request");
      return pendingRequests.get(cacheKey);
    }

    // Only set loading state when actually fetching
    commit("SET_LOADING_TRANSFERS", true);
    commit("CLEAR_ERROR");

    try {
      const params = {
        page: page || state.transfersPagination.page,
        per_page: perPage || state.transfersPagination.per_page,
        sort: "-created_at",
        ...filters,
      };

      // Store the promise for deduplication
      const requestPromise = http.get(`/v2/groups/${groupId}/transfers`, {
        params,
      });
      pendingRequests.set(cacheKey, requestPromise);

      const response = await requestPromise;
      const data = response.data || {};

      // Cache the result
      dataCache.set(cacheKey, {
        data,
        timestamp: Date.now(),
      });

      commit("SET_TRANSFERS", data.items || []);
      commit("SET_TRANSFERS_PAGINATION", {
        page: data.page,
        per_page: data.per_page,
        total: data.total,
        pages: data.pages,
      });

      // Clean up pending request
      pendingRequests.delete(cacheKey);

      return data;
    } catch (error) {
      const message =
        error?.response?.data?.error?.message || "Failed to load transfers";
      commit("SET_ERROR", message);
      throw error;
    } finally {
      commit("SET_LOADING_TRANSFERS", false);
    }
  },

  async fetchTransferDetails({ commit }, { groupId, transferId }) {
    commit("SET_LOADING_TRANSFER_DETAILS", true);
    commit("CLEAR_ERROR");

    try {
      const response = await http.get(
        `/v2/groups/${groupId}/transfers/${transferId}`,
      );
      commit("SET_TRANSFER_DETAILS", response.data);
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.error?.message ||
        "Failed to load transfer details";
      commit("SET_ERROR", message);
      throw error;
    } finally {
      commit("SET_LOADING_TRANSFER_DETAILS", false);
    }
  },

  // ==================== TRANSFER REQUESTS ====================

  async fetchTransferRequests(
    { commit, state },
    { groupId, filters = {}, page, perPage, forceRefresh = false } = {},
  ) {
    // Create cache key with stable filter serialization
    const stableFilters = Object.keys(filters)
      .sort()
      .map((key) => `${key}:${filters[key]}`)
      .join("|");
    const cacheKey = `transfer-requests-${groupId}-${stableFilters}-${
      page || state.requestsPagination.page
    }-${perPage || state.requestsPagination.per_page}`;

    // Check cache first (unless forceRefresh)
    console.log(`🔍 Checking cache for key: ${cacheKey}`);
    console.log(
      `📊 Cache size: ${dataCache.size}, Force refresh: ${forceRefresh}`,
    );

    if (!forceRefresh && dataCache.has(cacheKey)) {
      const cached = dataCache.get(cacheKey);
      const age = Date.now() - cached.timestamp;
      const maxAge = RESOURCE_CACHE.transferRequests.staleTime;

      console.log(
        `⏰ Cache age: ${Math.round(age / 1000)}s, Max age: ${Math.round(
          maxAge / 1000,
        )}s`,
      );

      if (age < maxAge) {
        console.log(
          `✅ Using cached transfer requests (${Math.round(
            age / 1000,
          )}s old) - No loading state`,
        );
        // Don't set loading state for cached data
        commit("SET_TRANSFER_REQUESTS", cached.data.items || []);
        commit("SET_REQUESTS_PAGINATION", {
          page: cached.data.page,
          per_page: cached.data.per_page,
          total: cached.data.total,
          pages: cached.data.pages,
        });
        return cached.data;
      } else {
        console.log(
          `♻️ Cache expired (${Math.round(
            age / 1000,
          )}s old), fetching fresh data`,
        );
        dataCache.delete(cacheKey);
      }
    } else {
      console.log(`❌ Cache miss for key: ${cacheKey}`);
    }

    // Check if already fetching (deduplication)
    if (pendingRequests.has(cacheKey)) {
      console.log("⏭️ Deduplicating transfer requests request");
      return pendingRequests.get(cacheKey);
    }

    // Only set loading state when actually fetching
    commit("SET_LOADING_TRANSFER_REQUESTS", true);
    commit("CLEAR_ERROR");

    try {
      const params = {
        page: page || state.requestsPagination.page,
        per_page: perPage || state.requestsPagination.per_page,
        sort: "-created_at",
        ...filters,
      };

      // Store the promise for deduplication
      const requestPromise = http.get(
        `/v2/groups/${groupId}/transfer-requests`,
        { params },
      );
      pendingRequests.set(cacheKey, requestPromise);

      const response = await requestPromise;
      const data = response.data || {};

      // Cache the result
      console.log(`💾 Caching transfer requests data for key: ${cacheKey}`);
      dataCache.set(cacheKey, {
        data,
        timestamp: Date.now(),
      });
      console.log(`📊 Cache size after setting: ${dataCache.size}`);

      commit("SET_TRANSFER_REQUESTS", data.items || []);
      commit("SET_REQUESTS_PAGINATION", {
        page: data.page,
        per_page: data.per_page,
        total: data.total,
        pages: data.pages,
      });

      // Clean up pending request
      pendingRequests.delete(cacheKey);

      return data;
    } catch (error) {
      const message =
        error?.response?.data?.error?.message ||
        "Failed to load transfer requests";
      commit("SET_ERROR", message);
      throw error;
    } finally {
      commit("SET_LOADING_TRANSFER_REQUESTS", false);
    }
  },

  async fetchTransferRequestDetails({ commit }, { groupId, requestId }) {
    commit("SET_LOADING_TRANSFER_REQUEST_DETAILS", true);
    commit("CLEAR_ERROR");

    try {
      const response = await http.get(
        `/v2/groups/${groupId}/transfer-requests/${requestId}`,
      );
      commit("SET_TRANSFER_REQUEST_DETAILS", response.data);
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.error?.message ||
        "Failed to load request details";
      commit("SET_ERROR", message);
      throw error;
    } finally {
      commit("SET_LOADING_TRANSFER_REQUEST_DETAILS", false);
    }
  },

  async createTransferRequest({ commit }, { groupId, payload }) {
    commit("CLEAR_ERROR");

    try {
      const response = await http.post(
        `/v2/groups/${groupId}/transfer-requests`,
        payload,
      );
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.error?.message ||
        "Failed to create transfer request";
      commit("SET_ERROR", message);
      throw error;
    }
  },

  async updateTransferRequestStatus(
    { commit },
    { groupId, requestId, status, note },
  ) {
    commit("CLEAR_ERROR");

    try {
      const response = await http.patch(
        `/v2/groups/${groupId}/transfer-requests/${requestId}/status`,
        { status, note },
      );

      // Update local state
      commit("UPDATE_REQUEST_STATUS", { requestId, status });

      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.error?.message ||
        "Failed to update request status";
      commit("SET_ERROR", message);
      throw error;
    }
  },

  // ==================== INVENTORY REMOVALS ====================

  async fetchRemovals(
    { commit, state },
    { storeId, filters = {}, page, perPage, forceRefresh = false } = {},
  ) {
    const pageNum = page || state.removalsPagination.page;
    const pageSize = perPage || state.removalsPagination.per_page;
    const stableFilters = Object.keys(filters)
      .sort()
      .map((key) => `${key}:${filters[key]}`)
      .join("|");
    const cacheKey = `removals-${storeId}-${stableFilters}-${pageNum}-${pageSize}`;

    // Check cache first (unless forceRefresh) - BEFORE setting loading state
    if (!forceRefresh && dataCache.has(cacheKey)) {
      const cached = dataCache.get(cacheKey);
      const age = Date.now() - cached.timestamp;
      const maxAge = RESOURCE_CACHE.removals.staleTime;

      if (age < maxAge) {
        console.log(
          `✅ Using cached removals (${Math.round(
            age / 1000,
          )}s old) - No loading state`,
        );
        // Don't set loading state for cached data
        commit("SET_REMOVALS", cached.data.items || []);
        commit("SET_REMOVALS_PAGINATION", {
          page: cached.data.page,
          per_page: cached.data.per_page,
          total: cached.data.total,
          pages: cached.data.pages,
        });
        commit("SET_REMOVALS_SUMMARY", cached.data.extras || {});
        return cached.data;
      } else {
        console.log(
          `♻️ Cache expired (${Math.round(
            age / 1000,
          )}s old), fetching fresh data`,
        );
        dataCache.delete(cacheKey);
      }
    }

    // Check if already fetching (deduplication)
    if (pendingRequests.has(cacheKey)) {
      console.log("⏭️ Deduplicating removals request");
      return pendingRequests.get(cacheKey);
    }

    // Only set loading state when actually fetching
    commit("SET_LOADING_REMOVALS", true);
    commit("CLEAR_ERROR");

    const requestPromise = (async () => {
      try {
        const params = {
          page: pageNum,
          per_page: pageSize,
          sort: "-created_at",
          ...filters,
        };

        const response = await http.get(
          `/v2/store/inventory/${storeId}/removals`,
          { params },
        );

        const data = response.data || {};

        // Cache the response
        dataCache.set(cacheKey, {
          data,
          timestamp: Date.now(),
        });

        commit("SET_REMOVALS", data.items || []);
        commit("SET_REMOVALS_PAGINATION", {
          page: data.page,
          per_page: data.per_page,
          total: data.total,
          pages: data.pages,
        });
        commit("SET_REMOVALS_SUMMARY", data.extras || {});

        return data;
      } catch (error) {
        const message =
          error?.response?.data?.error?.message ||
          "Failed to load inventory removals";
        commit("SET_ERROR", message);
        throw error;
      } finally {
        commit("SET_LOADING_REMOVALS", false);
        pendingRequests.delete(cacheKey);
      }
    })();

    pendingRequests.set(cacheKey, requestPromise);
    return requestPromise;
  },

  // ==================== FILTERS & PAGINATION ====================

  setTransferFilters({ commit }, filters) {
    commit("SET_TRANSFER_FILTERS", filters);
  },

  setRequestFilters({ commit }, filters) {
    commit("SET_REQUEST_FILTERS", filters);
  },

  setTransfersPage({ commit }, page) {
    commit("SET_TRANSFERS_PAGINATION", { page });
  },

  setRequestsPage({ commit }, page) {
    commit("SET_REQUESTS_PAGINATION", { page });
  },

  setRemovalsPage({ commit }, page) {
    commit("SET_REMOVALS_PAGINATION", { page });
  },

  // ==================== CACHE INVALIDATION ====================

  clearTransferCache({ commit }) {
    commit("SET_TRANSFERS", []);
    commit("SET_TRANSFER_DETAILS", null);
  },

  clearRequestCache({ commit }) {
    commit("SET_TRANSFER_REQUESTS", []);
    commit("SET_TRANSFER_REQUEST_DETAILS", null);
  },

  clearRemovalsCache({ commit }) {
    commit("SET_REMOVALS", []);
    commit("SET_REMOVALS_SUMMARY", null);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
